/**
 * Os eventos do funil depois do corte da medição (OS Medição, itens 8 e 9, dicionário da seção 10.2).
 *
 * A chave `medicaoCorte` (runtimeConfig, env NUXT_PUBLIC_MEDICAO_CORTE) decide:
 * - desligada: nada aqui dispara, e cada componente segue com os eventos de antes;
 * - ligada: os componentes chamam estas funções no lugar dos eventos antigos, e o `purchase` deixa
 *   de sair do front (passa a sair do servidor, com GA4_PURCHASE_ATIVO=true no backend).
 *
 * Regra: nenhum parâmetro leva texto livre. O que vem do servidor entra só como código curto
 * (`erro_codigo`); o resto é escolhido aqui.
 */

let corte = false;

/** Chamado uma vez pelo plugin `medicao-corte.client.ts`. */
export function definirCorte(ligado: boolean) {
  corte = ligado === true;
}

export function corteLigado(): boolean {
  return corte;
}

export type Etapa = "cadastro" | "login" | "pagamento";
export type Forma = "pix" | "boleto" | "cartao" | "transferencia" | "pix_recorrente";

type Params = Record<string, string | number | undefined>;

const FORMATO_DE_CODIGO = /^[a-z0-9_]{1,40}$/;

/** Código curto e estável; qualquer outra coisa (mensagem, HTML) vira "outro". */
export function codigo(valor: unknown): string {
  const texto = typeof valor === "string" ? valor.trim().toLowerCase() : "";
  return FORMATO_DE_CODIGO.test(texto) ? texto : "outro";
}

/** Valor do plano como número: "38,00" -> 38. Sem valor conhecido, undefined (o evento não leva 0). */
export function valorNumerico(valor: unknown): number | undefined {
  if (typeof valor === "number") return Number.isFinite(valor) && valor > 0 ? valor : undefined;
  if (typeof valor !== "string") return undefined;

  const texto = valor.trim().replace(/[^\d,.-]/g, "");
  // "1.234,56" e "38,00" (formato brasileiro) e "38.00".
  const normal = texto.includes(",") ? texto.replace(/\./g, "").replace(",", ".") : texto;
  const numero = Number(normal);

  return Number.isFinite(numero) && numero > 0 ? numero : undefined;
}

/** O id da venda que o servidor usa no `purchase`: A-{order} na assinatura, C-{order} nos créditos. */
export function transacaoDe(data: any, tipo: "assinatura" | "creditos"): string | undefined {
  if (typeof data?.transacao === "string" && /^[AC]-\d+$/.test(data.transacao)) return data.transacao;
  const orderId = data?.orderId;
  if (orderId === undefined || orderId === null || !/^\d+$/.test(String(orderId))) return undefined;
  return (tipo === "creditos" ? "C-" : "A-") + String(orderId);
}

function semVazios(params: Params): Params {
  const limpo: Params = {};
  for (const [chave, valor] of Object.entries(params)) {
    if (valor !== undefined && valor !== "") limpo[chave] = valor;
  }
  return limpo;
}

/** Empurra no dataLayer no formato do projeto: `{ event: nome, [nome]: params }`. */
function empurrar(evento: string, params: Params): boolean {
  if (!corte || typeof window === "undefined") return false;

  try {
    const w = window as any;
    w.dataLayer = w.dataLayer || [];
    w.dataLayer.push({ event: evento, [evento]: semVazios(params) });
    return true;
  } catch (_e) {
    // Medição nunca quebra a tela
    return false;
  }
}

function acionadorDe(valor: unknown): string {
  return typeof valor === "string" && valor ? codigoLivre(valor) : "link direto";
}

/** O acionador vem da URL; fica só com letras, números, hífen e espaço, e curto. */
function codigoLivre(valor: string): string {
  return valor.replace(/[^\w\- ]/g, "").slice(0, 60) || "link direto";
}

// --- Etapas --------------------------------------------------------------------------------

/** Grade ou carrossel de planos e créditos exibido. Substitui o view_cart e o begin_checkout de montagem. */
export function funilVerPlanos(lista: "planos" | "creditos", acionador?: unknown) {
  return empurrar("view_item_list", { item_list_name: lista, acionador: acionadorDe(acionador) });
}

/** Plano escolhido (clique). */
export function funilEscolherPlano(plano: string | undefined, tipo: "assinatura" | "creditos", acionador?: unknown) {
  return empurrar("select_plan", { plano, tipo, acionador: acionadorDe(acionador) });
}

/** Formulário de pagamento exibido. Substitui o add_payment_info de montagem. */
export function funilAbrirPagamento(forma: Forma, plano: string | undefined, valor: unknown, acionador?: unknown) {
  const numero = valorNumerico(valor);
  if (numero === undefined) return false;
  return empurrar("begin_checkout", { forma, plano, valor: numero, acionador: acionadorDe(acionador) });
}

/** Envio válido: passou na validação local e a requisição saiu. */
export function funilTentarPagamento(forma: Forma, plano: string | undefined, valor: unknown, acionador?: unknown) {
  return empurrar("add_payment_info", { forma, plano, valor: valorNumerico(valor), acionador: acionadorDe(acionador) });
}

/** Pix ou boleto criado. Substitui o purchase que saía na geração. */
export function funilCobrancaGerada(forma: Forma, plano: string | undefined, transacao: string | undefined, acionador?: unknown) {
  return empurrar("generate_payment", { forma, plano, transacao, acionador: acionadorDe(acionador) });
}

/** Cartão em análise no antifraude. */
export function funilPagamentoPendente(forma: Forma, transacao: string | undefined) {
  return empurrar("payment_pending", { forma, transacao });
}

/** Recusa financeira. */
export function funilPagamentoRecusado(forma: Forma | undefined, etapa: Etapa, codigoDoServidor: unknown) {
  return empurrar("payment_failed", { forma, etapa, codigo: codigo(codigoDoServidor) });
}

export function funilErroValidacao(etapa: Etapa, campo: unknown) {
  return empurrar("erro_validacao", { etapa, campo: codigo(campo) });
}

export function funilErroTecnico(etapa: Etapa, codigoDoErro: unknown) {
  return empurrar("erro_tecnico", { etapa, codigo: codigo(codigoDoErro) });
}

/**
 * A falha que voltou da API, separada pelo `erro_tipo` do contrato: validação, falha técnica ou
 * recusa. Sem `erro_tipo` (rede caiu, resposta antiga), decide pelo status HTTP.
 *
 * @param data   corpo da resposta (`err.response.data` ou `data`)
 * @param status status HTTP, quando houver
 */
export function funilErroDaApi(etapa: Etapa, data: any, status?: number, forma?: Forma) {
  const tipo = data?.erro_tipo;
  const codigoDoErro = data?.erro_codigo;

  if (tipo === "recusa") return funilPagamentoRecusado(forma, etapa, codigoDoErro);
  if (tipo === "validacao") return funilErroValidacao(etapa, codigoDoErro);
  if (tipo === "tecnico") return funilErroTecnico(etapa, codigoDoErro);

  if (!status) return funilErroTecnico(etapa, "rede");
  if (status >= 500) return funilErroTecnico(etapa, "http_" + status);
  return funilErroValidacao(etapa, "http_" + status);
}

/**
 * Conta criada agora: no checkout, o sucesso do storeCreateUserAssine (depois do código por
 * e-mail) ou a conta nova do login social. O `newUser` do auth/login não serve: lá ele quer dizer
 * "nunca teve pedido", e o auth/login não cria conta.
 */
export function funilCadastro(metodo: string) {
  return empurrar("sign_up", { method: codigo(metodo) });
}

/** Login de conta que já existia. */
export function funilLogin(metodo: string) {
  return empurrar("login", { method: codigo(metodo) });
}

/** Minha Conta → trocar forma de pagamento: não é venda, não é add_payment_info. */
export function funilTrocaMeio(forma: Forma) {
  return empurrar("troca_meio_pagamento", { forma });
}
